import { useState } from "react";

const InfoSVG = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
  </svg>
);

const GearSVG = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.57 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
  </svg>
);

const cardStyle = {
  background: "#f8fbff",
  borderRadius: "22px",
  padding: "18px",
  border: "2px solid #dbeafe",
  boxShadow: "0 8px 20px rgba(30, 58, 138, 0.10)",
  marginTop: "20px",
};

const iconBtnStyle = (active) => ({
  background: active ? "#e0f2fe" : "transparent",
  border: `1px solid ${active ? "#2563eb" : "#bfdbfe"}`,
  borderRadius: "8px",
  padding: "5px 7px",
  cursor: "pointer",
  color: active ? "#2563eb" : "#6b7280",
  display: "flex",
  alignItems: "center",
});

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: "8px",
  border: "1px solid #bfdbfe",
  fontSize: "14px",
  boxSizing: "border-box",
  background: "#fff",
  color: "#1e3a5f",
};

export default function WeeklyHabitsWidget({
  habits,
  completions,
  weekKey,
  currentDateKey,
  onAdd,
  onRemove,
  onManualUndo,
  onRemoveSource,
  onAddHabit,
  onUpdateHabit,
  onDeleteHabit,
  disabled,
  activeSideQuests,
  isAdmin,
}) {
  const [showSettings, setShowSettings] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [formTitle, setFormTitle] = useState("");
  const [formTarget, setFormTarget] = useState(3);
  const [formXP, setFormXP] = useState(50);
  const [formLinked, setFormLinked] = useState([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [editingHabit, setEditingHabit] = useState(null); // { id, title, target, xp, linked }
  const [circleInfo, setCircleInfo] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState(null);

  const questTitles = (activeSideQuests || []).map((q) => q.title);

  function handleSubmitForm() {
    if (!formTitle.trim()) return;
    onAddHabit({
      title: formTitle.trim(),
      targetCount: Math.max(1, formTarget),
      xpReward: Math.max(0, formXP),
      linkedSideQuests: formLinked,
      createdBy: isAdmin ? "admin" : "user",
    });
    setFormTitle("");
    setFormTarget(3);
    setFormXP(50);
    setFormLinked([]);
  }

  function toggleLinked(title) {
    setFormLinked((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    );
  }

  function handleCircleClick(habit, index, weekData) {
    if (disabled) return;
    const sources = weekData.sources || [];
    const count = weekData.count || 0;
    const isFilled = index < count;
    if (isFilled) {
      setCircleInfo({ habitId: habit.id, index, source: sources[index] || "manual" });
    } else if (index === count) {
      const alreadyLoggedToday = sources.some((s) => s === `manual:${currentDateKey}`);
      if (!alreadyLoggedToday) {
        onAdd(habit.id, `manual:${currentDateKey}`);
      }
    }
  }

  function handleConfirmRemove() {
    if (!removeConfirm) return;
    const sourceKey = removeConfirm.source || "";
    if (sourceKey.startsWith("manual:")) {
      onManualUndo(removeConfirm.habitId, sourceKey.replace("manual:", ""));
    } else {
      onRemoveSource(removeConfirm.habitId, sourceKey);
    }
    setRemoveConfirm(null);
    setCircleInfo(null);
  }

  function formatSource(source) {
    if (!source) return "Manual log";
    if (source.startsWith("manual:")) {
      const date = new Date(`${source.replace("manual:", "")}T12:00:00`);
      return `Manual log · ${date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}`;
    }
    if (source.startsWith("sidequest:")) {
      const parts = source.replace("sidequest:", "").split(":");
      const title = parts[0];
      const dateKey = parts[1];
      if (dateKey) {
        const date = new Date(`${dateKey}T12:00:00`);
        return `Side Quest: ${title} · ${date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}`;
      }
      return `Side Quest: ${title}`;
    }
    return source;
  }

  function renderHeader() {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: showSettings || showInfo ? "14px" : "14px" }}>
        <h3 style={{ margin: 0, color: "#1e3a5f", fontSize: "20px" }}>🎯 Weekly Goals</h3>
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          <button type="button" onClick={() => { setShowInfo((p) => !p); setShowSettings(false); }} style={iconBtnStyle(showInfo)}>
            <InfoSVG />
          </button>
          <button type="button" onClick={() => { setShowSettings((p) => !p); setShowInfo(false); }} style={iconBtnStyle(showSettings)}>
            <GearSVG />
          </button>
        </div>
      </div>
    );
  }

  function renderInfoPanel() {
    return (
      <div style={{ background: "#eff6ff", borderRadius: "12px", padding: "12px 14px", border: "1px solid #bfdbfe", marginBottom: "14px", fontSize: "13px", color: "#4b6b8a", lineHeight: 1.6 }}>
        <strong style={{ color: "#1e3a5f", display: "block", marginBottom: "4px" }}>About Weekly Goals</strong>
        Track recurring habits over a week — like "4 workouts per week." Tap the next empty circle each time you complete it. Goals can be linked to Side Quests so they count automatically.
      </div>
    );
  }

  function renderSettingsPanel() {
    return (
      <div style={{ display: "grid", gap: "14px" }}>
        {/* Existing goals list */}
        {habits && habits.length > 0 && (
          <div style={{ background: "#f0f8ff", borderRadius: "12px", padding: "12px", border: "1px solid #bfdbfe" }}>
            <div style={{ fontSize: "11px", fontWeight: "bold", color: "#4b6b8a", textTransform: "uppercase", marginBottom: "8px" }}>Current Goals</div>
            <div style={{ display: "grid", gap: "8px" }}>
              {habits.map((habit) => (
                <div key={habit.id}>
                  {editingHabit?.id === habit.id ? (
                    /* Inline edit form */
                    <div style={{ background: "#fff", borderRadius: "10px", padding: "12px", border: "2px solid #2563eb" }}>
                      <div style={{ display: "grid", gap: "8px" }}>
                        <div>
                          <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", marginBottom: "4px" }}>Goal Title</div>
                          <input type="text" value={editingHabit.title} onChange={(e) => setEditingHabit((p) => ({ ...p, title: e.target.value }))} style={inputStyle} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                          <div>
                            <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", marginBottom: "4px" }}>Times/week</div>
                            <input type="number" min="1" max="14" value={editingHabit.target} onChange={(e) => setEditingHabit((p) => ({ ...p, target: Number(e.target.value) }))} style={inputStyle} />
                          </div>
                          <div>
                            <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", marginBottom: "4px" }}>XP Reward</div>
                            <input type="number" min="0" step="5" value={editingHabit.xp} onChange={(e) => setEditingHabit((p) => ({ ...p, xp: Number(e.target.value) }))} style={inputStyle} />
                          </div>
                        </div>
                        {questTitles.length > 0 && (
                          <div>
                            <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", marginBottom: "6px" }}>Linked Side Quests</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                              {questTitles.map((title) => {
                                const sel = editingHabit.linked.includes(title);
                                return (
                                  <button key={title} type="button"
                                    onClick={() => setEditingHabit((p) => ({ ...p, linked: sel ? p.linked.filter((t) => t !== title) : [...p.linked, title] }))}
                                    style={{ padding: "4px 10px", borderRadius: "8px", border: sel ? "2px solid #38bdf8" : "1px solid #bfdbfe", background: sel ? "#e0f2fe" : "#f8fbff", color: sel ? "#0369a1" : "#4b6b8a", fontSize: "12px", fontWeight: sel ? "bold" : "normal", cursor: "pointer" }}>
                                    {title}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button type="button"
                            onClick={() => { if (!editingHabit.title.trim()) return; onUpdateHabit(editingHabit.id, { title: editingHabit.title.trim(), targetCount: Math.max(1, editingHabit.target), xpReward: Math.max(0, editingHabit.xp), linkedSideQuests: editingHabit.linked }); setEditingHabit(null); }}
                            disabled={!editingHabit.title.trim()}
                            style={{ padding: "6px 14px", borderRadius: "8px", border: "none", background: editingHabit.title.trim() ? "#2563eb" : "#bfdbfe", color: "#fff", fontWeight: "bold", fontSize: "12px", cursor: editingHabit.title.trim() ? "pointer" : "default" }}>
                            Save
                          </button>
                          <button type="button" onClick={() => setEditingHabit(null)}
                            style={{ padding: "6px 12px", borderRadius: "8px", border: "1px solid #bfdbfe", background: "#f5faff", color: "#4b6b8a", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Normal habit row */
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", borderRadius: "10px", padding: "8px 10px", border: "1px solid #dbeafe" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "13px" }}>{habit.title}</div>
                        <div style={{ fontSize: "11px", color: "#94a3b8" }}>{habit.targetCount}×/week · +{habit.xpReward} XP</div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
                        <button type="button"
                          onClick={() => { setEditingHabit({ id: habit.id, title: habit.title, target: habit.targetCount, xp: habit.xpReward, linked: habit.linkedSideQuests || [] }); setConfirmDeleteId(null); }}
                          style={{ padding: "3px 8px", borderRadius: "6px", border: "1px solid #bfdbfe", background: "#f0f8ff", color: "#2563eb", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>
                          Edit
                        </button>
                        {onDeleteHabit && (
                          confirmDeleteId === habit.id ? (
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <span style={{ fontSize: "11px", color: "#ef4444", fontWeight: "bold" }}>Delete?</span>
                              <button type="button" onClick={() => { onDeleteHabit(habit.id); setConfirmDeleteId(null); }}
                                style={{ padding: "2px 8px", borderRadius: "6px", border: "none", background: "#ef4444", color: "#fff", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>Yes</button>
                              <button type="button" onClick={() => setConfirmDeleteId(null)}
                                style={{ padding: "2px 8px", borderRadius: "6px", border: "1px solid #bfdbfe", background: "#f5faff", color: "#4b6b8a", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>No</button>
                            </div>
                          ) : (
                            <button type="button" onClick={() => setConfirmDeleteId(habit.id)}
                              style={{ background: "none", border: "none", color: "#f87171", fontSize: "16px", cursor: "pointer", padding: "0 4px", lineHeight: 1 }}>×</button>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add new goal form */}
        <div style={{ padding: "12px", borderRadius: "12px", background: "#f0f9ff", border: "1px solid #bfdbfe" }}>
          <div style={{ fontSize: "11px", fontWeight: "bold", color: "#4b6b8a", textTransform: "uppercase", marginBottom: "10px" }}>Add New Goal</div>
          <div style={{ display: "grid", gap: "10px" }}>
            <div>
              <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", marginBottom: "4px" }}>Goal Title</div>
              <input
                type="text" value={formTitle} onChange={(e) => setFormTitle(e.target.value)}
                placeholder="e.g. Workouts per week"
                style={inputStyle}
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              <div>
                <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", marginBottom: "4px" }}>Times per week</div>
                <input type="number" min="1" max="14" value={formTarget} onChange={(e) => setFormTarget(Number(e.target.value))} style={inputStyle} />
              </div>
              <div>
                <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", marginBottom: "4px" }}>XP Reward</div>
                <input type="number" min="0" step="5" value={formXP} onChange={(e) => setFormXP(Number(e.target.value))} style={inputStyle} />
              </div>
            </div>
            {questTitles.length > 0 && (
              <div>
                <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", marginBottom: "6px" }}>Linked Side Quests</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {questTitles.map((title) => {
                    const selected = formLinked.includes(title);
                    return (
                      <button key={title} type="button" onClick={() => toggleLinked(title)} style={{
                        padding: "4px 10px", borderRadius: "8px",
                        border: selected ? "2px solid #38bdf8" : "1px solid #bfdbfe",
                        background: selected ? "#e0f2fe" : "#f8fbff",
                        color: selected ? "#0369a1" : "#4b6b8a",
                        fontSize: "12px", fontWeight: selected ? "bold" : "normal", cursor: "pointer",
                      }}>{title}</button>
                    );
                  })}
                </div>
              </div>
            )}
            <button type="button" onClick={handleSubmitForm} disabled={!formTitle.trim()} style={{
              padding: "8px 16px", borderRadius: "10px", border: "none",
              background: formTitle.trim() ? "#2563eb" : "#bfdbfe", color: "#fff",
              fontWeight: "bold", fontSize: "13px", cursor: formTitle.trim() ? "pointer" : "default", alignSelf: "flex-start",
            }}>Add Goal</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      {renderHeader()}
      {showInfo && renderInfoPanel()}
      {showSettings ? (
        renderSettingsPanel()
      ) : (
        <>
          {(!habits || habits.length === 0) ? (
            <div style={{ color: "#94a3b8", fontSize: "13px" }}>
              No weekly goals set yet. Tap the <GearSVG /> button to add one.
            </div>
          ) : (
            <div style={{ display: "grid", gap: "10px" }}>
              {habits.map((habit) => {
                const weekData = completions?.[habit.id]?.[weekKey] || {};
                const count = weekData.count || 0;
                const sources = weekData.sources || [];
                const completed = count >= habit.targetCount;

                return (
                  <div key={habit.id} style={{
                    background: completed ? "#f0fdf4" : "#fff",
                    borderRadius: "14px", padding: "12px 14px",
                    border: completed ? "2px solid #86efac" : "1px solid #dbeafe",
                  }}>
                    {/* Header */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
                      <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px", flex: 1 }}>{habit.title}</div>
                      {completed ? (
                        <div style={{ background: "#dcfce7", border: "1px solid #86efac", borderRadius: "999px", padding: "2px 10px", fontSize: "11px", fontWeight: "bold", color: "#15803d", whiteSpace: "nowrap", flexShrink: 0 }}>
                          ✨ +{habit.xpReward} XP
                        </div>
                      ) : (
                        <div style={{ fontSize: "11px", color: "#94a3b8", whiteSpace: "nowrap", flexShrink: 0 }}>+{habit.xpReward} XP</div>
                      )}
                    </div>

                    {/* Circles + count */}
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                      {Array.from({ length: habit.targetCount }).map((_, i) => {
                        const isFilled = i < count;
                        const isNextEmpty = i === count;
                        const isInfoCircle = circleInfo?.habitId === habit.id && circleInfo?.index === i;
                        const isClickable = !disabled && (isFilled || isNextEmpty);
                        return (
                          <button key={i} type="button"
                            onClick={() => handleCircleClick(habit, i, weekData)}
                            title={isFilled ? "Tap to see info" : isNextEmpty ? "Tap to log" : ""}
                            style={{
                              width: "26px", height: "26px", borderRadius: "50%",
                              border: isFilled ? "2px solid #2563eb" : isNextEmpty ? "2px solid #93c5fd" : "2px solid #bfdbfe",
                              background: isFilled ? "#2563eb" : "#f0f8ff",
                              cursor: isClickable ? "pointer" : "default",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              transition: "all 0.15s ease", flexShrink: 0, padding: 0,
                              outline: isInfoCircle ? "2px solid #f59e0b" : "none",
                            }}
                          >
                            {isFilled && <span style={{ color: "#fff", fontSize: "12px", fontWeight: "bold" }}>✓</span>}
                          </button>
                        );
                      })}
                      <div style={{ fontSize: "12px", color: completed ? "#15803d" : "#4b6b8a", fontWeight: completed ? "bold" : "normal", marginLeft: "2px" }}>
                        {count}/{habit.targetCount}
                      </div>
                    </div>

                    {/* Circle info popup */}
                    {circleInfo?.habitId === habit.id && !removeConfirm && (
                      <div style={{ marginTop: "8px", background: "#eff6ff", borderRadius: "10px", padding: "10px 12px", border: "1.5px solid #bfdbfe", fontSize: "12px" }}>
                        <div style={{ color: "#1e3a5f", fontWeight: "bold", marginBottom: "6px" }}>
                          {formatSource(circleInfo.source)}
                        </div>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button type="button"
                            onClick={() => setRemoveConfirm({ habitId: habit.id, index: circleInfo.index, source: circleInfo.source })}
                            style={{ padding: "4px 10px", borderRadius: "7px", border: "1.5px solid #fca5a5", background: "#fef2f2", color: "#b91c1c", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>
                            Uncheck
                          </button>
                          <button type="button" onClick={() => setCircleInfo(null)}
                            style={{ padding: "4px 10px", borderRadius: "7px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#4b6b8a", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>
                            Close
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Remove confirmation */}
                    {removeConfirm?.habitId === habit.id && (
                      <div style={{ marginTop: "8px", background: "#fff1f2", borderRadius: "10px", padding: "10px 12px", border: "1.5px solid #fca5a5", fontSize: "12px" }}>
                        <div style={{ color: "#b91c1c", fontWeight: "bold", marginBottom: "6px" }}>Remove this completion?</div>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button type="button" onClick={handleConfirmRemove}
                            style={{ padding: "4px 10px", borderRadius: "7px", border: "none", background: "#ef4444", color: "#fff", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>
                            Yes, remove
                          </button>
                          <button type="button" onClick={() => { setRemoveConfirm(null); setCircleInfo(null); }}
                            style={{ padding: "4px 10px", borderRadius: "7px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#4b6b8a", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {habit.linkedSideQuests && habit.linkedSideQuests.length > 0 && (
                      <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "6px" }}>
                        Auto-counts: {habit.linkedSideQuests.join(", ")}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
